import { SwitchMode } from "@/components/switch-mode";
import { ThemedText } from "@/components/themed-text";
import { BrandButton } from "@/components/ui/brand-button";
import { SUPPORT_MAIL } from "@/constants";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { showAlert } from "@/lib/alert";
import { getEcho } from "@/lib/echo";
import { ensureEchoConnectionBound } from "@/lib/echo-connection";
import { isCustomerRole } from "@/lib/roles";
import { stripeConnectService } from "@/lib/services/stripe-connect";
import MaterialCommunityIcons from "@react-native-vector-icons/material-design-icons";
import Ionicons from "@react-native-vector-icons/ionicons";
import { router, useFocusEffect } from "expo-router";
import React, { useCallback, useEffect, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

export default function CompleteRegistrationScreen() {
  const { user, signOut, refreshUser, theme } = useAuth();
  const [stripeLoading, setStripeLoading] = useState(false);

  useFocusEffect(
    useCallback(() => {
      refreshUser();
    }, []),
  );

  useEffect(() => {
    if (!user?.id || isCustomerRole(user?.role)) return;

    const echo = getEcho();
    if (!echo) return;

    ensureEchoConnectionBound(echo);

    const channelName = `driver.verification.${user.id}`;
    echo.private(channelName).listen(".DriverVerificationUpdated", () => {
      refreshUser();
    });

    return () => {
      echo.leave(channelName);
    };
  }, [user?.id, user?.role]);

  const isDriverVerified = user?.driver?.is_verified;
  const isStripeCompleted =
    user?.stripe_account?.details_submitted &&
    user?.stripe_account?.charges_enabled;
  const isVehicleAdded = (user?.vehicles_count || 0) > 0;

  // If both are completed, redirect to home
  useEffect(() => {
    if (isCustomerRole(user?.role)) {
      router.replace("/(tabs)");
    }
    if (isStripeCompleted && isVehicleAdded && isDriverVerified) {
      router.replace("/(tabs)");
    }
  }, [isStripeCompleted, isVehicleAdded, isDriverVerified, user]);

  const handleCompleteStripe = async () => {
    setStripeLoading(true);
    try {
      // First create/get account
      await stripeConnectService.createAccount();
      // Then get onboarding link
      const { url } = await stripeConnectService.getOnboardingLink();
      // Open in browser
      router.push({
        pathname: "/stripe-onboarding",
        params: {
          url,
        },
      });
    } catch (error: any) {
      showAlert(
        "Error",
        error.response?.data?.message || "Failed to connect with Stripe",
      );
    } finally {
      setStripeLoading(false);
    }
  };

  const handleAddVehicle = () => {
    router.push("/vehicles/create?addVehicle=true");
  };

  const handleVerifyDriver = () => {
    router.push("/profile/license");
  };

  const handleLogout = async () => {
    showAlert("Logout", "Are you sure you want to logout?", [
      { text: "Cancel", style: "cancel" },
      { text: "Logout", onPress: async () => await signOut() },
    ]);
  };

  const steps = [
    {
      title: "Vehicle details",
      description: "Register your vehicle and upload the required documents.",
      isCompleted: isVehicleAdded,
      icon: "truck-outline",
      onPress: handleAddVehicle,
      buttonLabel: "Add vehicle",
    },
    {
      title: "Driver information",
      description: "Confirm your driving and business information.",
      isCompleted: isDriverVerified,
      icon: "account-outline",
      onPress: handleVerifyDriver,
      buttonLabel: user?.driver?.document
        ? user?.driver?.document?.rejection_reason
          ? "Update driver licence"
          : "Document is under review"
        : "Add driver licence",
      rejectionReason: user?.driver?.document?.rejection_reason,
    },
    {
      title: "Payout details",
      description: "Securely connect your bank account through Stripe.",
      isCompleted: isStripeCompleted,
      icon: "credit-card-outline",
      onPress: handleCompleteStripe,
      buttonLabel:
        user?.stripe_account === null
          ? "Connect payouts"
          : "Complete Stripe setup",
      loading: stripeLoading,
    },
  ];

  const completedCount = steps.filter((step) => step.isCompleted).length;
  const progress = completedCount / steps.length;

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.background }]}
    >
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.header}>
          <ThemedText style={styles.eyebrow} type="smallSemiBold">
            DRIVER ACTIVATION
          </ThemedText>
          <ThemedText style={styles.title} type="title">
            Get ready to start earning
          </ThemedText>
          <ThemedText
            lightColor={BrandColors.darkGray}
            darkColor={BrandColors.mediumGray}
          >
            Complete these three steps to activate your Auto Transporter driver
            account.
          </ThemedText>
        </View>

        <View style={[styles.progressSection, { borderColor: theme.border }]}>
          <View style={styles.progressRow}>
            <ThemedText type="smallSemiBold">
              {completedCount} of {steps.length} complete
            </ThemedText>
            <ThemedText
              type="extraSmall"
              lightColor={BrandColors.darkGray}
              darkColor={BrandColors.mediumGray}
            >
              Your progress is saved automatically
            </ThemedText>
          </View>
          <View
            style={[styles.progressTrack, { backgroundColor: theme.border }]}
          >
            <View
              style={[styles.progressFill, { width: `${progress * 100}%` }]}
            />
          </View>
        </View>

        <View style={styles.stepsContainer}>
          {steps.map((step, index) => (
            <View key={index} style={styles.stepCard}>
              <View style={styles.stepIndicator}>
                <View
                  style={[
                    styles.numberCircle,
                    { borderColor: theme.border },
                    step.isCompleted && styles.completedCircle,
                  ]}
                >
                  {step.isCompleted ? (
                    <Ionicons
                      name="checkmark"
                      size={18}
                      color={BrandColors.white}
                    />
                  ) : (
                    <ThemedText
                      type="smallSemiBold"
                      lightColor={BrandColors.darkGray}
                      darkColor={BrandColors.mediumGray}
                    >
                      {index + 1}
                    </ThemedText>
                  )}
                </View>
                {index < steps.length - 1 && (
                  <View
                    style={[styles.stepLine, { backgroundColor: theme.border }]}
                  />
                )}
              </View>

              <View style={styles.iconTile}>
                <MaterialCommunityIcons
                  name={step.icon as any}
                  size={22}
                  color={BrandColors.primary}
                />
              </View>

              <View style={styles.stepContent}>
                <ThemedText style={styles.stepTitle} type="subtitle">
                  {step.title}
                </ThemedText>
                <ThemedText
                  style={styles.stepDescription}
                  type="small"
                  lightColor={BrandColors.darkGray}
                  darkColor={BrandColors.mediumGray}
                >
                  {step.description}
                </ThemedText>
                {step.rejectionReason && (
                  <ThemedText style={styles.error} type="small">
                    Please upload a valid document.{"\n"}Reason:{" "}
                    {step.rejectionReason}
                  </ThemedText>
                )}
                {!step.isCompleted ? (
                  <TouchableOpacity
                    style={styles.stepLink}
                    onPress={step.onPress}
                    disabled={step.loading}
                    activeOpacity={0.7}
                  >
                    <ThemedText style={styles.stepLinkText} type="smallSemiBold">
                      {step.buttonLabel}
                    </ThemedText>
                    {step.loading ? (
                      <ActivityIndicator
                        size="small"
                        color={BrandColors.primary}
                      />
                    ) : (
                      <Ionicons
                        name="chevron-forward"
                        size={16}
                        color={BrandColors.primary}
                      />
                    )}
                  </TouchableOpacity>
                ) : (
                  <View style={styles.completedBadge}>
                    <Ionicons
                      name="checkmark-circle"
                      size={16}
                      color={BrandColors.success}
                    />
                    <ThemedText
                      style={styles.completedText}
                      type="smallSemiBold"
                    >
                      Completed
                    </ThemedText>
                  </View>
                )}
              </View>
            </View>
          ))}
        </View>

        {isStripeCompleted && isVehicleAdded && isDriverVerified && (
          <View style={styles.stepsFooter}>
            <ThemedText type="small">
              Your account is ready to start earning.
            </ThemedText>
            <BrandButton
              onPress={() => router.replace("/(tabs)")}
              variant="primary"
            >
              Go to Home
            </BrandButton>
          </View>
        )}

        <TouchableOpacity
          style={[styles.helpRow, { borderColor: theme.border }]}
          onPress={async () => await Linking.openURL(`mailto:${SUPPORT_MAIL}`)}
          activeOpacity={0.7}
        >
          <Ionicons
            name="help-circle-outline"
            size={22}
            color={BrandColors.primary}
          />
          <View style={styles.helpContent}>
            <ThemedText type="small">Need help getting set up?</ThemedText>
            <ThemedText
              type="extraSmall"
              lightColor={BrandColors.darkGray}
              darkColor={BrandColors.mediumGray}
            >
              Contact our driver support team.
            </ThemedText>
          </View>
        </TouchableOpacity>

        <View style={styles.footer}>
          <TouchableOpacity onPress={handleLogout} activeOpacity={0.7}>
            <ThemedText style={styles.logoutText} type="small">
              Log out
            </ThemedText>
          </TouchableOpacity>
          <SwitchMode isText={true} style={styles.switchMode} />
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: BrandColors.white,
  },
  scrollContent: {
    padding: 24,
    flexGrow: 1,
    width: "100%",
    maxWidth: 700,
    marginHorizontal: "auto",
  },
  header: {
    gap: 8,
    marginBottom: 24,
  },
  eyebrow: {
    color: BrandColors.primary,
    letterSpacing: 0.5,
  },
  title: {
    fontSize: 30,
    lineHeight: 38,
  },
  progressSection: {
    borderTopWidth: 1,
    borderBottomWidth: 1,
    paddingVertical: 16,
    gap: 10,
    marginBottom: 28,
  },
  progressRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },
  progressTrack: {
    height: 6,
    borderRadius: 3,
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    borderRadius: 3,
    backgroundColor: BrandColors.primary,
  },
  stepsContainer: {
    marginBottom: 12,
  },
  stepCard: {
    flexDirection: "row",
    gap: 16,
  },
  stepIndicator: {
    alignItems: "center",
    width: 40,
  },
  numberCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  completedCircle: {
    backgroundColor: BrandColors.success,
    borderColor: BrandColors.success,
  },
  stepLine: {
    width: 1,
    flex: 1,
  },
  iconTile: {
    width: 40,
    height: 40,
    borderRadius: 10,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: BrandColors.primaryLight,
  },
  stepContent: {
    flex: 1,
    paddingTop: 6,
    paddingBottom: 28,
  },
  stepTitle: {
    fontSize: 18,
    marginBottom: 6,
  },
  stepDescription: {
    lineHeight: 22,
    marginBottom: 16,
  },
  error: {
    color: BrandColors.error,
    marginBottom: 16,
  },
  stepLink: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    alignSelf: "flex-start",
  },
  stepLinkText: {
    color: BrandColors.primary,
  },
  completedBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: "#F0FDF4",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
    alignSelf: "flex-start",
  },
  completedText: {
    fontSize: 12,
    color: BrandColors.success,
  },
  stepsFooter: {
    alignItems: "center",
    gap: 20,
    marginBottom: 24,
  },
  helpRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    paddingVertical: 16,
  },
  helpContent: {
    flex: 1,
  },
  footer: {
    paddingTop: 32,
    alignItems: "center",
    gap: 20,
    marginBottom: 20,
  },
  logoutText: {
    color: BrandColors.mediumGray,
  },
  switchMode: {
    width: "100%",
  },
});
